import React, { useState } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { green } from '@/shared/styles/colors';
import ScrollableView from '@/shared/components/ScrollableView';
import useStore from '@/stores/useStore';
import ClientIntro from '@/components/instantStyling/client/ClientIntro';
import ClientInstantStylingModal from '@/components/instantStyling/client/ClientInstantStylingModal';
import StylistInstantStyling from '@/components/instantStyling/stylist/StylistInstantStyling';
import { useShallow } from 'zustand/react/shallow';
import { useVideoProvider } from '@/shared/hooks/useVideoProvider';
import SentryService from '@/services/sentry/Sentry.service';
import { FlexCenterScreen } from '@/shared/styles/styledComponents/styledComponents';
import { ActivityIndicator } from 'react-native-paper';
import useInstantStylingStore from '@/stores/useInstantStylingStore';
import { router } from 'expo-router';
import { ROUTES } from '@/shared/constants/routes';

const InstantStyling = () => {
  const { startSession, joinSession } = useVideoProvider();

  const { isStylist, activeSessionName } = useStore(
    useShallow((state) => ({
      isStylist: state.isStylist,
      activeSessionName: state.activeSessionName,
    })),
  );

  const { matchStatus, resetMatchStatus } = useInstantStylingStore(
    useShallow((state) => ({
      matchStatus: state.matchStatus,
      resetMatchStatus: state.resetMatchStatus,
    })),
  );

  const [isLoading, setIsLoading] = useState(false);

  const handleStartSession = async (guestId: number) => {
    try {
      setIsLoading(true);

      if (!matchStatus?.consultationId) {
        throw new Error('Start session: Consultation ID is not available');
      }

      await startSession(guestId, matchStatus.consultationId);
      resetMatchStatus();
      router.push(ROUTES.VIDEO_SESSION.path);
    } catch (error) {
      SentryService.captureError('Failed to start video session', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinSession = async () => {
    try {
      setIsLoading(true);
      await joinSession(activeSessionName);
      resetMatchStatus();
      router.push(ROUTES.VIDEO_SESSION.path);
    } catch (error) {
      SentryService.captureError('Failed to join video session', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <ScrollableView
        contentContainerStyle={
          isStylist
            ? stylistScrollViewContainerStyle
            : clientScrollViewContainerStyle
        }
      >
        {isLoading ? (
          <FlexCenterScreen>
            <ActivityIndicator size="large" />
          </FlexCenterScreen>
        ) : isStylist ? (
          <StylistInstantStyling handleStartSession={handleStartSession} />
        ) : (
          <ClientIntro />
        )}
      </ScrollableView>
      <ClientInstantStylingModal handleJoinSession={handleJoinSession} />
    </>
  );
};

export default InstantStyling;

const clientScrollViewContainerStyle: StyleProp<ViewStyle> = {
  flexGrow: 1,
  alignItems: 'center',
  backgroundColor: green,
};

const stylistScrollViewContainerStyle: StyleProp<ViewStyle> = {
  flexGrow: 1,
  alignItems: 'center',
};
